/**
 * Plain-language meaning of every rule code, for the decision screens.
 *
 * The sentences are copied from backend/checkpoint_presentation.py RULE_MEANINGS so the desk, the
 * phone card, the Guide and the saved checkpoint summaries all say the same thing. Keep the two
 * lists in step. `ruleTip` in lib/format.js keeps the technical threshold text for the Thresholds page.
 */
export const RULE_MEANINGS = {
  L1: "The gap between buyers' and sellers' prices was too wide.",
  L2: "At least one option price failed freshness or validity checks.",
  L3: "There was not enough recorded trading activity in an option.",
  L4: "The option prices were positioned too far from the index level.",
  E1: "The estimated payoff after costs was not attractive enough for the risk.",
  E2: "The possible reward was too small compared with the possible loss.",
  E3: "The estimated chance of profit was below this setup's required minimum.",
  T1: "The estimated risk of reaching an unfavourable price was too high.",
  T2: "The scheduled-event check did not clear: an event was too close or its calendar could not be verified.",
  T3: "Higher short-term volatility required stronger evidence.",
  C1: "The market had not shown a sufficiently consistent direction.",
  C2: "Not enough market indicators agreed on the direction.",
  C3: "Other markets were showing a conflicting or unusually large move.",
  B1: "The stricter checks required before the market opens were not met.",
  B2: "The required confidence level or another mandatory entry check was not met.",
  "DATA-01": "The system could not verify usable, up-to-date option prices for a qualifying trade.",
  "KS-01": "An essential market input was unavailable.",
  "KS-02": "The data's date, expiry or timezone did not match the session.",
  "KS-03": "The forecast failed an internal consistency check.",
  "KS-05": "A forecast value failed a safety check and needs review.",
  "KS-06": "A major scheduled event was still pending during the trading session.",
  "KS-07": "A configured account-risk limit was reached.",
  KILL: "The session's safety stop was active; new entries were refused.",
  EDGE: "The setup did not match all of the saved entry conditions.",
  // Frontend-only: the runner records this safety block when a run fires after its cutoff.
  LATE: "The run was evaluated after its scheduled time, so it could not enter.",
};

/** The plain sentence for a code, or null when the code is unknown. */
export const ruleMeaning = (code) => (code ? RULE_MEANINGS[code] || null : null);

/** Plain names for the rule groups, keyed by the letters in front of the number. */
export const RULE_GROUPS = {
  L: "Option prices and liquidity",
  E: "Payoff and odds",
  T: "Risk and scheduled events",
  C: "Market confirmation",
  B: "Entry gate",
  S: "Costs and slippage",
  DATA: "Data check",
  KS: "Safety stop",
  KILL: "Safety stop",
  EDGE: "Saved entry conditions",
  LATE: "Timing",
};

/** "C2" → "Market confirmation"; "KS-07" → "Safety stop". */
export const ruleGroupName = (code) => {
  const key = String(code || "").replace(/[-\d]+$/, "");
  return RULE_GROUPS[key] || RULE_GROUPS[String(code || "")] || "Rule";
};

/** Codes in the order they appear on the Guide's "Codes you will see" table. */
export const RULE_CODES_IN_ORDER = [
  "L1", "L2", "L3", "L4",
  "E1", "E2", "E3",
  "T1", "T2", "T3",
  "C1", "C2", "C3",
  "B1", "B2",
  "DATA-01", "EDGE", "LATE",
  "KS-01", "KS-02", "KS-03", "KS-05", "KS-06", "KS-07", "KILL",
];
